/**
 * The page's side of the media worker (SEO_BRIEF_04 item 7b): take a video's audio out, in the
 * browser, as a `File` ready for the B6 upload.
 *
 * The worker is started on first use and kept for the tab, like the subtitle worker. It is a
 * separate bundle, so a page that never sees a video never downloads mediabunny.
 */

import type { AudioExtractResult } from '../../../../../src/media/audio-extract.ts';
import type { MediaReply, MediaRequest } from './media-protocol.ts';

/** What the page gets: the audio as a `File`, or the refusal with its reason. */
export type AudioForUpload =
  | {
      readonly ok: true;
      readonly file: File;
      readonly codec: string;
      readonly container: string;
      readonly durationS: number | null;
    }
  | Extract<AudioExtractResult, { ok: false }>;

/** Same build-id rule as the subtitle worker (`lib/worker.ts`): never yesterday's bundle. */
function mediaWorkerUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="build-id"]');
  const build = meta?.content.trim();
  return build
    ? `/static/build/base/media-worker.js?v=${encodeURIComponent(build)}`
    : '/static/build/base/media-worker.js';
}

let shared: Worker | null = null;
let nextId = 1;
const waiting = new Map<
  number,
  { resolve: (r: AudioExtractResult) => void; onProgress?: (fraction: number) => void }
>();

function start(): Worker {
  if (shared) return shared;
  const worker = new Worker(mediaWorkerUrl(), { type: 'module' });
  worker.onmessage = (event: MessageEvent<MediaReply>) => {
    const reply = event.data;
    const entry = waiting.get(reply.id);
    if (!entry) return;
    if (reply.kind === 'progress') {
      entry.onProgress?.(reply.fraction);
      return;
    }
    waiting.delete(reply.id);
    entry.resolve(reply.result);
  };
  worker.onerror = (event) => {
    // A worker that failed to load (offline, blocked) answers every waiting request with the
    // same refusal rather than leaving the page on a spinner.
    event.preventDefault();
    for (const [id, entry] of waiting) {
      waiting.delete(id);
      entry.resolve({
        ok: false,
        reason: 'unreadable',
        detail: 'media worker failed',
        codec: null,
      });
    }
    shared = null;
    worker.terminate();
  };
  shared = worker;
  return worker;
}

/**
 * The video's audio, copied into an audio file. Never rejects. `signal` cancels it: the worker
 * stops at the next packet and answers `cancelled`.
 */
export function audioForUpload(
  video: File,
  options: {
    readonly maxBytes: number | null;
    readonly onProgress?: (fraction: number) => void;
    readonly signal?: AbortSignal;
  },
): Promise<AudioForUpload> {
  const worker = start();
  const id = nextId++;
  const answer = new Promise<AudioExtractResult>((resolve) => {
    waiting.set(id, {
      resolve,
      ...(options.onProgress ? { onProgress: options.onProgress } : {}),
    });
  });
  options.signal?.addEventListener(
    'abort',
    () => worker.postMessage({ kind: 'cancel', id } satisfies MediaRequest),
    { once: true },
  );
  worker.postMessage({
    kind: 'extractAudio',
    id,
    file: video,
    inputName: video.name,
    maxBytes: options.maxBytes,
  } satisfies MediaRequest);
  return answer.then((result) =>
    result.ok
      ? {
          ok: true,
          file: new File([result.bytes as Uint8Array<ArrayBuffer>], result.name, {
            type: result.mime,
          }),
          codec: result.codec,
          container: result.container,
          durationS: result.durationS,
        }
      : result,
  );
}
